import React, { useMemo } from 'react'
import {
  DatePosition,
  getClampedPercentXForDate,
  getPercentXForDate,
} from '../hooks/useDatePositions'
import { GanttColors, GanttDimensions } from '../constants'
import {
  CriticalPathLinkData,
  ProcessPeriod,
  useCriticalPathContext,
} from './CriticalPathContext'
import { FallbackLinkInfo } from '../utils/visibilityFilters'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'

interface CriticalPathLinesProps {
  datePositions: DatePosition[]
  criticalPathLinks: CriticalPathLinkData[]
  partialLinks: FallbackLinkInfo<CriticalPathLinkData>[]
  currentRowIndex: number
  onLineClick?: (link: CriticalPathLinkData, event: React.MouseEvent) => void
  onPartialLineClick?: (
    info: FallbackLinkInfo<CriticalPathLinkData>,
    event: React.MouseEvent
  ) => void
  barHalfHeight: number
  chartHeight: number
}

export const CriticalPathLines = React.memo<CriticalPathLinesProps>(
  ({
    datePositions,
    criticalPathLinks,
    partialLinks,
    currentRowIndex,
    onLineClick,
    onPartialLineClick,
    barHalfHeight,
    chartHeight,
  }) => {
    // 端点が別の行（描画中の行から見えない行、折りたたまれた行）にあっても判定できるよう、
    // 工程バーの期間は各行が context に登録したものを参照する
    const processPeriods = useCriticalPathContext()?.processPeriods

    // Compute bar edge Y percentages from actual dimensions
    const barEdgePercent = useMemo(() => {
      if (chartHeight <= 0) return { top: 10, bottom: 90 }
      const top = ((chartHeight / 2 - barHalfHeight) / chartHeight) * 100
      const bottom = ((chartHeight / 2 + barHalfHeight) / chartHeight) * 100
      return { top, bottom }
    }, [chartHeight, barHalfHeight])

    // Build elements and collect used colors in a single pass
    const { elements, markerDefs } = useMemo(() => {
      const result: React.ReactNode[] = []
      // 編集可CP（丸＋シェブロン矢印）と編集不可CP（菱形＋塗り三角）でマーカーを分ける
      const usedColors = new Set<string>()
      const usedReadonlyColors = new Set<string>()

      criticalPathLinks.forEach((link, linkIndex) => {
        const handleClick = onLineClick
          ? (e: React.MouseEvent) => onLineClick(link, e)
          : undefined
        const lineColor = link.color || GanttColors.CRITICAL_PATH_LINE
        const isReadonly = link.editable === false
        if (isReadonly) {
          usedReadonlyColors.add(lineColor)
        } else {
          usedColors.add(lineColor)
        }
        const markerId = isReadonly
          ? `critical-path-triangle-${currentRowIndex}-${lineColor.replace('#', '')}`
          : `critical-path-arrow-${currentRowIndex}-${lineColor.replace('#', '')}`
        const linkElements = generateCriticalPathElements(
          link,
          linkIndex,
          datePositions,
          currentRowIndex,
          markerId,
          barEdgePercent,
          handleClick,
          lineColor,
          undefined,
          undefined,
          isReadonly,
          isLinkOutOfProcessPeriod(link, processPeriods)
        )
        result.push(...linkElements)
      })

      // Partial links: draw lines up to terminus row with dashed ending
      partialLinks.forEach((info, idx) => {
        const { link, from, to } = info
        const handleClick = onPartialLineClick
          ? (e: React.MouseEvent) => onPartialLineClick(info, e)
          : undefined
        const lineColor = link.color || GanttColors.CRITICAL_PATH_LINE
        const isReadonly = link.editable === false
        if (isReadonly) {
          usedReadonlyColors.add(lineColor)
        } else {
          usedColors.add(lineColor)
        }

        // Determine direction based on resolved row positions.
        const isFromAbove = from.rowIndex <= to.rowIndex

        // Ensure terminus is in the correct direction from the visible endpoint.
        // Only adjusts when ancestor resolves to the same row or crosses over.
        let effectiveFromRow = from.rowIndex
        let effectiveToRow = to.rowIndex
        if (isFromAbove && effectiveToRow <= effectiveFromRow) {
          effectiveToRow = effectiveFromRow + 1
        } else if (!isFromAbove && effectiveFromRow <= effectiveToRow) {
          effectiveFromRow = effectiveToRow + 1
        }
        const terminusRowIndex = from.isFallback
          ? effectiveFromRow
          : effectiveToRow

        // When from side is hidden (isFallback), the visible target should still show an arrow
        const markerId = from.isFallback
          ? isReadonly
            ? `critical-path-triangle-${currentRowIndex}-${lineColor.replace('#', '')}`
            : `critical-path-arrow-${currentRowIndex}-${lineColor.replace('#', '')}`
          : ''

        const linkElements = generateCriticalPathElements(
          {
            ...link,
            fromRowIndex: effectiveFromRow,
            toRowIndex: effectiveToRow,
          },
          criticalPathLinks.length + idx,
          datePositions,
          currentRowIndex,
          markerId,
          barEdgePercent,
          handleClick,
          lineColor,
          terminusRowIndex,
          isFromAbove,
          isReadonly,
          isLinkOutOfProcessPeriod(link, processPeriods)
        )
        result.push(...linkElements)
      })

      // Build marker definitions for each unique color
      const markers = [
        // 編集可CPの終点: シェブロン矢印
        ...Array.from(usedColors).map(color => (
          <marker
            key={`arrow-${color}`}
            id={`critical-path-arrow-${currentRowIndex}-${color.replace('#', '')}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="4"
            markerHeight="4"
            orient="auto-start-reverse"
          >
            <path
              d="M 0 0 L 8 5 L 0 10"
              fill="none"
              stroke={color}
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </marker>
        )),
        // 編集不可CP（プロジェクト側）の終点: 塗りつぶし三角
        // markerUnits=userSpaceOnUse で線幅に依存しない絶対pxサイズにする
        ...Array.from(usedReadonlyColors).map(color => (
          <marker
            key={`triangle-${color}`}
            id={`critical-path-triangle-${currentRowIndex}-${color.replace('#', '')}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerUnits="userSpaceOnUse"
            markerWidth="8"
            markerHeight="8"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 Z" fill={color} />
          </marker>
        )),
        // 編集不可CP（プロジェクト側）の始点: 正方形の菱形（幅8px×高さ8px）
        // viewBox と viewport を正方形に揃えることで、回転した正方形（菱形）として表示する
        ...Array.from(usedReadonlyColors).map(color => (
          <marker
            key={`diamond-${color}`}
            id={`critical-path-diamond-${currentRowIndex}-${color.replace('#', '')}`}
            viewBox="0 0 10 10"
            refX="5"
            refY="5"
            markerUnits="userSpaceOnUse"
            markerWidth="8"
            markerHeight="8"
          >
            <path d="M 5 0 L 10 5 L 5 10 L 0 5 Z" fill={color} />
          </marker>
        )),
      ]

      return { elements: result, markerDefs: markers }
    }, [
      datePositions,
      criticalPathLinks,
      partialLinks,
      currentRowIndex,
      onLineClick,
      onPartialLineClick,
      barEdgePercent,
      processPeriods,
    ])

    if (currentRowIndex < 0) return null

    return (
      <g className="critical-path-lines" strokeLinecap="round">
        <defs>{markerDefs}</defs>
        {elements}
      </g>
    )
  }
)

CriticalPathLines.displayName = 'CriticalPathLines'

// ============================================================================
// Types
// ============================================================================

type RowRole = 'source' | 'intermediate' | 'target'

type BarEdgePercent = { top: number; bottom: number }

// ============================================================================
// Out-of-period Detection
// ============================================================================

const OUT_OF_PERIOD_DASH_PATTERN = '3 3'

// 予定期間が未設定の工程（未スケジュール、または期間を登録済みの行が存在しない
// プログラム管理の他プロジェクト工程）は「期間外」と判定しない。
// 位置計算のような today へのフォールバックを入れると、未スケジュール工程の
// CP がすべて点線になってしまうため。
function isOutOfProcessPeriod(
  date: CalendarDateVO,
  period?: ProcessPeriod
): boolean {
  const { start, end } = period ?? {}
  if (!start || !end) return false
  return date.isBefore(start) || date.isAfter(end)
}

// 片方の端点だけが工程期間から外れていてもリンク全体を点線にする。
// 「この CP は不整合」はリンク単位の状態であり、かつ端点の行が折りたたまれていても
// 中間行・祖先行の線に警告が残る必要があるため。
function isLinkOutOfProcessPeriod(
  link: CriticalPathLinkData,
  processPeriods?: ReadonlyMap<string, ProcessPeriod>
): boolean {
  if (!processPeriods) return false
  return (
    isOutOfProcessPeriod(
      link.fromDate,
      processPeriods.get(link.fromWbsItemUuid)
    ) ||
    isOutOfProcessPeriod(link.toDate, processPeriods.get(link.toWbsItemUuid))
  )
}

// ============================================================================
// Element Generation
// ============================================================================

function generateCriticalPathElements(
  link: CriticalPathLinkData,
  linkIndex: number,
  datePositions: DatePosition[],
  currentRowIndex: number,
  markerId: string,
  barEdge: BarEdgePercent,
  onClick?: (e: React.MouseEvent) => void,
  lineColor: string = GanttColors.CRITICAL_PATH_LINE,
  terminusRowIndex?: number,
  isFromAboveOverride?: boolean,
  isReadonly?: boolean,
  isOutOfPeriod?: boolean
): React.ReactNode[] {
  const { fromRowIndex, toRowIndex } = link
  const elements: React.ReactNode[] = []

  const minRow = Math.min(fromRowIndex, toRowIndex)
  const maxRow = Math.max(fromRowIndex, toRowIndex)
  if (currentRowIndex < minRow || currentRowIndex > maxRow) return elements

  // Skip when both dates are outside the visible range on the same side
  if (
    getPercentXForDate(datePositions, link.fromDate, 'center') === null &&
    getPercentXForDate(datePositions, link.toDate, 'center') === null
  ) {
    const clampedFrom = getClampedPercentXForDate(
      datePositions,
      link.fromDate,
      'center'
    )
    const clampedTo = getClampedPercentXForDate(
      datePositions,
      link.toDate,
      'center'
    )
    if (
      clampedFrom === null ||
      clampedTo === null ||
      clampedFrom === clampedTo
    ) {
      return elements
    }
  }

  const isFromAboveTo = isFromAboveOverride ?? fromRowIndex < toRowIndex
  const rowRole = getRowRole(currentRowIndex, fromRowIndex, toRowIndex)
  const isTerminus =
    terminusRowIndex !== undefined && currentRowIndex === terminusRowIndex
  const keyPrefix = `critical-path-${linkIndex}`

  const { CRITICAL_PATH_LINE_WIDTH, CRITICAL_PATH_CIRCLE_RADIUS } =
    GanttDimensions
  const DASHED_PATTERN = '4 6'
  // 工程期間外の線に付ける属性。terminus（相手行が非表示）の点線と意味が異なるため
  // 細かいパターンで区別する。親 g の strokeLinecap="round" のままだと線幅ぶん破線が
  // 伸びて隙間が潰れるため butt に戻す
  const outOfPeriodProps: {
    strokeDasharray?: string
    strokeLinecap?: 'butt'
  } = isOutOfPeriod
    ? { strokeDasharray: OUT_OF_PERIOD_DASH_PATTERN, strokeLinecap: 'butt' }
    : {}

  // Y positions computed from actual bar dimensions
  const barTopY = `${barEdge.top}%`
  const barBottomY = `${barEdge.bottom}%`
  const topEdgeY = '0%'
  const bottomEdgeY = '100%'

  // For source: exit from bottom if target is below, top if above
  const sourceExitY = isFromAboveTo ? barBottomY : barTopY
  // For target: arrow tip at the bar edge
  const targetEntryY = isFromAboveTo ? barTopY : barBottomY

  // Turn level: fixed near the row edge for the horizontal segment
  const turnY = isFromAboveTo ? '8%' : '92%'

  // Terminus row: draw a dashed vertical stub (no circle, no arrow).
  // Direction depends on whether the terminus is on the from or to side:
  // - from-side terminus (source hidden): dashed stub from center toward target
  // - to-side terminus (target hidden): dashed stub from source direction to center
  if (isTerminus) {
    const fromX = getClampedPercentXForDate(
      datePositions,
      link.fromDate,
      'center'
    )
    if (fromX !== null) {
      const isFromSideTerminus = currentRowIndex === fromRowIndex
      const dashedY1 = isFromSideTerminus
        ? '50%'
        : isFromAboveTo
          ? topEdgeY
          : bottomEdgeY
      const dashedY2 = isFromSideTerminus
        ? isFromAboveTo
          ? bottomEdgeY
          : topEdgeY
        : '50%'

      elements.push(
        <g
          key={`${keyPrefix}-terminus`}
          onClick={onClick}
          style={{ cursor: onClick ? 'pointer' : undefined }}
        >
          <line
            x1={`${fromX}%`}
            y1={dashedY1}
            x2={`${fromX}%`}
            y2={dashedY2}
            stroke={lineColor}
            strokeWidth={CRITICAL_PATH_LINE_WIDTH}
            strokeDasharray={DASHED_PATTERN}
            {...outOfPeriodProps}
          />
          {/* Transparent hit area */}
          <line
            x1={`${fromX}%`}
            y1={dashedY1}
            x2={`${fromX}%`}
            y2={dashedY2}
            stroke="transparent"
            strokeWidth={12}
            style={{ pointerEvents: 'stroke' }}
          />
        </g>
      )
    }
    return elements
  }

  switch (rowRole) {
    case 'source': {
      // Skip when fromDate is outside visible range (bar not visible)
      const fromX = getPercentXForDate(datePositions, link.fromDate, 'center')
      if (fromX !== null) {
        elements.push(
          <g
            key={`${keyPrefix}-source`}
            onClick={onClick}
            style={{ cursor: onClick ? 'pointer' : undefined }}
          >
            {/* Starting shape at fromDate position, at bar edge（編集可: 丸 / 編集不可: 菱形） */}
            {!isReadonly && (
              <circle
                cx={`${fromX}%`}
                cy={sourceExitY}
                r={CRITICAL_PATH_CIRCLE_RADIUS}
                fill={lineColor}
              />
            )}
            {/* Vertical line from bar edge to row boundary */}
            <line
              x1={`${fromX}%`}
              y1={sourceExitY}
              x2={`${fromX}%`}
              y2={isFromAboveTo ? bottomEdgeY : topEdgeY}
              stroke={lineColor}
              strokeWidth={CRITICAL_PATH_LINE_WIDTH}
              {...outOfPeriodProps}
              markerStart={
                isReadonly
                  ? `url(#critical-path-diamond-${currentRowIndex}-${lineColor.replace('#', '')})`
                  : undefined
              }
            />
            {/* Transparent hit area */}
            <line
              x1={`${fromX}%`}
              y1={sourceExitY}
              x2={`${fromX}%`}
              y2={isFromAboveTo ? bottomEdgeY : topEdgeY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
          </g>
        )
      }
      break
    }

    case 'intermediate': {
      // Vertical line passing through the entire row (only when fromDate is in visible range)
      const fromX = getPercentXForDate(datePositions, link.fromDate, 'center')
      if (fromX !== null) {
        elements.push(
          <g
            key={`${keyPrefix}-intermediate`}
            onClick={onClick}
            style={{ cursor: onClick ? 'pointer' : undefined }}
          >
            <line
              x1={`${fromX}%`}
              y1={topEdgeY}
              x2={`${fromX}%`}
              y2={bottomEdgeY}
              stroke={lineColor}
              strokeWidth={CRITICAL_PATH_LINE_WIDTH}
              {...outOfPeriodProps}
            />
            {/* Transparent hit area */}
            <line
              x1={`${fromX}%`}
              y1={topEdgeY}
              x2={`${fromX}%`}
              y2={bottomEdgeY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
          </g>
        )
      }
      break
    }

    case 'target': {
      const fromX = getClampedPercentXForDate(
        datePositions,
        link.fromDate,
        'center'
      )
      const toX = getClampedPercentXForDate(
        datePositions,
        link.toDate,
        'center'
      )

      if (fromX !== null && toX !== null) {
        const entryY = isFromAboveTo ? topEdgeY : bottomEdgeY
        const isToDateInRange =
          getPercentXForDate(datePositions, link.toDate, 'center') !== null

        elements.push(
          <g
            key={`${keyPrefix}-target`}
            onClick={onClick}
            style={{ cursor: onClick ? 'pointer' : undefined }}
          >
            {/* 1. Vertical from row edge to turn level (at fromX) */}
            <line
              x1={`${fromX}%`}
              y1={entryY}
              x2={`${fromX}%`}
              y2={turnY}
              stroke={lineColor}
              strokeWidth={CRITICAL_PATH_LINE_WIDTH}
              {...outOfPeriodProps}
            />
            {/* 2. Horizontal from fromX to toX (at turn level) */}
            <line
              x1={`${fromX}%`}
              y1={turnY}
              x2={`${toX}%`}
              y2={turnY}
              stroke={lineColor}
              strokeWidth={CRITICAL_PATH_LINE_WIDTH}
              {...outOfPeriodProps}
            />
            {/* 3. Vertical from turn level to bar entry with arrow (skip when toDate is outside visible range) */}
            {isToDateInRange && (
              <line
                x1={`${toX}%`}
                y1={turnY}
                x2={`${toX}%`}
                y2={targetEntryY}
                stroke={lineColor}
                strokeWidth={CRITICAL_PATH_LINE_WIDTH}
                {...outOfPeriodProps}
                // 丸キャップだと線端がマーカー（三角）の先から僅かにはみ出るため butt にする
                strokeLinecap="butt"
                markerEnd={`url(#${markerId})`}
              />
            )}
            {/* Transparent hit areas */}
            <line
              x1={`${fromX}%`}
              y1={entryY}
              x2={`${fromX}%`}
              y2={turnY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
            <line
              x1={`${fromX}%`}
              y1={turnY}
              x2={`${toX}%`}
              y2={turnY}
              stroke="transparent"
              strokeWidth={12}
              style={{ pointerEvents: 'stroke' }}
            />
            {isToDateInRange && (
              <line
                x1={`${toX}%`}
                y1={turnY}
                x2={`${toX}%`}
                y2={targetEntryY}
                stroke="transparent"
                strokeWidth={12}
                style={{ pointerEvents: 'stroke' }}
              />
            )}
          </g>
        )
      }
      break
    }
  }

  return elements
}

// ============================================================================
// Utility
// ============================================================================

function getRowRole(
  currentRowIndex: number,
  fromRowIndex: number,
  toRowIndex: number
): RowRole {
  if (currentRowIndex === fromRowIndex) return 'source'
  if (currentRowIndex === toRowIndex) return 'target'
  return 'intermediate'
}
